// Chat history store cho widget broker (Approach B).
// Tách hoàn toàn với chat/chat-store (Approach A) để compare fair.

import { createSignal, createEffect } from "solid-js";
import { getClaims } from "../../lib/auth";
import type { ChatMessage } from "../../lib/agent-sdk";

interface UiChatMessage extends ChatMessage {
  ts?: number;
  tool_trace?: Array<{ name: string; ok: boolean; error?: string }>;
  /// Mark message là final summary từ AI sau khi tools xong. UI render
  /// với "✓ Done" header để phân biệt với assistant bubble bình thường.
  summary?: boolean;
  /// Mark message là câu hỏi AI gửi user (ask_user tool). UI render
  /// với "?" icon + amber styling.
  ask_question?: boolean;
}

function keyFor(sub: string | undefined | null): string {
  return `booking-control:chat-broker:${sub ?? "anon"}`;
}

function load(sub: string | undefined | null): UiChatMessage[] {
  try {
    const raw = localStorage.getItem(keyFor(sub));
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    return arr.filter((m): m is UiChatMessage => m && typeof m.role === "string");
  } catch {
    return [];
  }
}

function save(sub: string | undefined | null, msgs: UiChatMessage[]) {
  try {
    const trimmed = msgs.slice(-200);
    localStorage.setItem(keyFor(sub), JSON.stringify(trimmed));
  } catch {
    /* quota */
  }
}

const [messages, setMessagesInner] = createSignal<UiChatMessage[]>(load(getClaims()?.sub));
const [openSignal, setOpen] = createSignal(false);
const [sending, setSending] = createSignal(false);

createEffect(() => {
  save(getClaims()?.sub, messages());
});

export function chatMessages() {
  return messages();
}

export function chatOpen() {
  return openSignal();
}

export function setChatOpen(v: boolean) {
  setOpen(v);
}

export function chatSending() {
  return sending();
}

export function setChatSending(v: boolean) {
  setSending(v);
}

export function appendMessage(m: UiChatMessage) {
  setMessagesInner((prev) => [...prev, { ...m, ts: m.ts ?? Date.now() }]);
}

export function replaceLast(m: UiChatMessage) {
  setMessagesInner((prev) => {
    if (prev.length === 0) return [{ ...m, ts: m.ts ?? Date.now() }];
    return [...prev.slice(0, -1), { ...m, ts: m.ts ?? Date.now() }];
  });
}

export function updateLastAssistantContent(content: string, trace?: UiChatMessage["tool_trace"]) {
  setMessagesInner((prev) => {
    if (prev.length === 0 || prev[prev.length - 1].role !== "assistant") {
      return [...prev, { role: "assistant", content, tool_trace: trace, ts: Date.now() }];
    }
    const last = prev[prev.length - 1];
    return [...prev.slice(0, -1), { ...last, content, tool_trace: trace ?? last.tool_trace }];
  });
}

export function clearChat() {
  setMessagesInner([]);
}

export type { UiChatMessage };
